import { useState, useEffect, useRef, useCallback, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FiClock, FiGrid, FiBell, FiSearch, FiLogOut } from "react-icons/fi";
import { FaUser } from "react-icons/fa";
import Constants from "../Constants.json";
import { navigateToStockDashboard } from "../utils/stockDashboardNav";
import { MARKET_SUBNAV_LINKS } from "./MarketPageSubNav";
import AuthContext from "../modules/Core/store/auth-context";
import useAppLanguage from "../hooks/useAppLanguage";
import {
  GLOBAL_NAV,
  navLabel,
  searchPlaceholder,
  searchingLabel,
  noResultsLabel,
} from "../i18n/globalNavLabels";

const C = { gold: "#b59a5b" };
const NAV_TXT = "rgba(255,255,255,.62)";
const NAV0 = "#2e574f";
const NAV1 = "#3d7068";
const ROW_H = 60;

function pathActive(pathname, href) {
  if (href === "/") return pathname === "/";
  if (href === "/capital") return pathname === "/capital" || pathname === "/capital/";
  if (href === "/marketflow") return pathname === "/marketflow";
  return pathname === href || pathname.startsWith(href + "/");
}

function groupActive(pathname, item) {
  if (item.href && pathActive(pathname, item.href)) return true;
  if (item.children) return item.children.some((c) => pathActive(pathname, c.href));
  return false;
}

function NavIconBtn({ title, ariaLabel, onClick, children, className = "" }) {
  return (
    <button
      type="button"
      title={title}
      aria-label={ariaLabel || title}
      onClick={onClick}
      className={`inv-nav-icon-btn ${className}`.trim()}
    >
      {children}
    </button>
  );
}

function NavSearch({ onPick, language }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [hits, setHits] = useState([]);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const fn = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);
  useEffect(() => {
    const query = String(q || "").trim();
    if (query.length < 2) { setHits([]); setLoading(false); return; }
    setLoading(true);
    const isLocal = typeof window !== "undefined" && (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");
    const base = isLocal ? "http://127.0.0.1:8000/api" : (Constants.BACKEND_SERVER_BASE_URL || "").replace(/\/$/, "");
    if (!base) { setHits([]); setLoading(false); return; }
    const id = window.setTimeout(() => {
      fetch(base + "/capitalflow/search?q=" + encodeURIComponent(query))
        .then((r) => (r.ok ? r.json() : Promise.reject()))
        .then((j) => setHits(Array.isArray(j?.results) ? j.results.slice(0, 6) : []))
        .catch(() => setHits([]))
        .finally(() => setLoading(false));
    }, 220);
    return () => window.clearTimeout(id);
  }, [q]);
  const go = (sym) => { if (!sym) return; onPick(sym); setQ(""); setOpen(false); };
  return (
    <div ref={ref} className="inv-nav-search-wrap">
      <div className="inv-nav-search-inner">
        <FiSearch size={18} color="rgba(255,255,255,.55)" style={{ flexShrink: 0 }} />
        <input
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && q.trim()) go(hits[0]?.symbol || q.trim().toUpperCase());
            if (e.key === "Escape") { setOpen(false); setQ(""); }
          }}
          placeholder={searchPlaceholder(language)}
          className="inv-nav-search-input"
        />
      </div>
      {open && (q.trim() || hits.length) ? (
        <div className="inv-nav-search-dropdown">
          {loading ? <div className="inv-nav-search-hit muted">{searchingLabel(language)}</div> : null}
          {!loading && hits.length === 0 && q.trim().length >= 2 ? (
            <div className="inv-nav-search-hit muted">{noResultsLabel(language)}</div>
          ) : null}
          {hits.map((h, i) => (
            <button key={h.symbol || i} type="button" onClick={() => go(h.symbol)} className="inv-nav-search-hit">
              <div className="inv-nav-search-sym">{h.symbol}</div>
              {h.name ? <div className="inv-nav-search-name">{h.name}</div> : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function useNavStyles() {
  useEffect(() => {
    if (document.getElementById("inv-global-nav-css")) return;
    const s = document.createElement("style");
    s.id = "inv-global-nav-css";
    s.textContent = `
:root{--inv-nav-h:108px;}
.inv-global-nav-desktop{display:none!important;}
@media(min-width:768px){
  .inv-global-nav-desktop{display:flex!important;}
  .inv-global-nav-links{flex:1;align-items:stretch;height:${ROW_H}px;}
  .inv-global-nav-actions{align-items:center;gap:8px;flex-shrink:0;}
}
.inv-global-nav-mobile-btn{display:flex;}
@media(min-width:768px){.inv-global-nav-mobile-btn{display:none!important;}}
.inv-nav-icon-btn{
  width:42px;height:42px;border-radius:10px;border:1px solid rgba(255,255,255,.2);
  background:rgba(255,255,255,.14);color:#fff;font-size:18px;cursor:pointer;
  display:flex;align-items:center;justify-content:center;flex-shrink:0;padding:0;
  transition:background .15s,border-color .15s,transform .15s;
}
.inv-nav-icon-btn:hover{background:rgba(255,255,255,.24);border-color:rgba(255,255,255,.32);transform:translateY(-1px);}
.inv-nav-lang-btn{font-size:13px;font-weight:800;letter-spacing:.04em;font-family:'IBM Plex Mono',monospace;}
.inv-nav-search-wrap{position:relative;flex:1;max-width:280px;min-width:140px;}
.inv-nav-search-inner{
  display:flex;align-items:center;gap:8px;height:42px;
  background:rgba(255,255,255,.13);border:1px solid rgba(255,255,255,.2);
  border-radius:10px;padding:0 14px;
}
.inv-nav-search-input{
  flex:1;background:transparent;border:none;outline:none;color:#fff;
  font-size:14px;font-weight:600;min-width:0;
}
.inv-nav-search-input::placeholder{color:rgba(255,255,255,.45);}
.inv-nav-search-dropdown{
  position:absolute;top:calc(100% + 6px);left:0;right:0;background:#fff;
  border-radius:10px;border:1px solid rgba(30,40,34,.1);
  box-shadow:0 8px 24px rgba(0,0,0,.18);overflow:hidden;z-index:300;
}
.inv-nav-search-hit{
  display:flex;width:100%;padding:10px 12px;background:none;border:none;
  border-bottom:1px solid rgba(30,40,34,.08);text-align:left;cursor:pointer;align-items:center;
}
.inv-nav-search-hit:hover{background:rgba(181,154,91,.08);}
.inv-nav-search-hit.muted{font-size:11px;color:#6f7b74;cursor:default;}
.inv-nav-search-sym{font-family:IBM Plex Mono,monospace;font-size:12px;font-weight:800;color:#1f2a24;}
.inv-nav-search-name{font-size:11px;color:#6f7b74;margin-left:8px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.inv-mkt-subnav{
  display:flex;flex-wrap:wrap;gap:10px 28px;align-items:center;
  padding:12px 0 14px;border-top:1px solid rgba(255,255,255,.12);
}
.inv-mkt-subnav-btn{
  background:none;border:none;font-size:15px;font-weight:900;
  color:rgba(255,255,255,.55);cursor:pointer;padding:5px 0;position:relative;
  font-family:inherit;transition:color .15s;
}
.inv-mkt-subnav-btn:hover{color:#fff;}
.inv-mkt-subnav-btn.active{color:#fff;}
.inv-mkt-subnav-btn.active::after{
  content:'';position:absolute;bottom:0;left:0;right:0;height:3px;
  background:linear-gradient(90deg,#b59a5b,#d4b87a);border-radius:2px 2px 0 0;
}
@media(max-width:640px){
  :root{--inv-nav-h:96px;}
  .inv-mkt-subnav{gap:8px 16px;padding:10px 0 12px;}
  .inv-mkt-subnav-btn{font-size:13px;}
}
`;
    document.head.appendChild(s);
  }, []);
}

export default function InvelpsGlobalNav({ darkMode, onToggleDark }) {
  useNavStyles();
  const { language, toggleLanguage, isEn } = useAppLanguage();
  const authCtx = useContext(AuthContext);
  const isLoggedIn = authCtx?.isLoggedIn;
  const location = useLocation();
  const navigate = useNavigate();
  const pathname = location.pathname || "/";
  const [menu, setMenu] = useState(false);
  const [mktOpen, setMktOpen] = useState(false);
  const mktRef = useRef(null);
  const openTicker = useCallback((sym) => navigateToStockDashboard(navigate, sym, "nav"), [navigate]);
  const go = (href) => { navigate(href); setMenu(false); setMktOpen(false); };

  useEffect(() => {
    const fn = (e) => { if (mktRef.current && !mktRef.current.contains(e.target)) setMktOpen(false); };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);

  const handleAuthClick = () => {
    if (isLoggedIn) {
      authCtx.logout();
      navigate("/");
    } else {
      navigate("/login");
    }
  };

  const langBtnTitle = isEn ? "Passer en français" : "Switch to English";
  const darkTitle = isEn
    ? (darkMode ? "Light mode" : "Dark mode")
    : (darkMode ? "Mode clair" : "Mode sombre");
  const loginTitle = isEn ? (isLoggedIn ? "Logout" : "Login") : (isLoggedIn ? "Déconnexion" : "Connexion");

  const btnStyle = (active) => ({
    background: "none", border: "none", padding: "0 16px", height: ROW_H, fontSize: 15, fontWeight: 800,
    color: active ? "#fff" : NAV_TXT,
    borderBottom: active ? "2.5px solid " + C.gold : "2.5px solid transparent", transition: "color .15s",
    cursor: "pointer",
  });

  const subnavLabel = (link) => (isEn ? link.labelEn : link.labelFr);

  return (
    <nav style={{ position: "sticky", top: 0, zIndex: 200, background: "linear-gradient(150deg," + NAV0 + "," + NAV1 + ")", boxShadow: "0 2px 20px rgba(16,40,28,.24)" }}>
      <div style={{ maxWidth: "min(1920px, 100%)", margin: "0 auto", padding: "0 clamp(14px, 2vw, 28px)" }}>
        <div style={{ display: "flex", alignItems: "center", height: ROW_H, gap: 12 }}>
          <button type="button" onClick={() => go("/capital")} style={{ background: "none", border: "none", display: "flex", alignItems: "center", gap: 8, flexShrink: 0, padding: 0, cursor: "pointer" }}>
            <div style={{ width: 3, height: 28, background: C.gold, borderRadius: 2 }} />
            <span style={{ fontFamily: "Cormorant Garamond, Georgia, serif", fontSize: 22, fontWeight: 700, color: C.gold, letterSpacing: "0.12em", lineHeight: 1 }}>INVELPS</span>
          </button>

          <div className="inv-global-nav-desktop inv-global-nav-links">
            {GLOBAL_NAV.map((item) => item.children ? (
              <div key={item.id} ref={mktRef} style={{ position: "relative", display: "inline-flex", height: ROW_H, alignItems: "center" }}>
                <button type="button" onClick={() => setMktOpen((o) => !o)} style={{ ...btnStyle(groupActive(pathname, item)), display: "flex", alignItems: "center", gap: 6 }}>
                  {navLabel(item, language)} <span style={{ fontSize: 10, opacity: 0.7 }}>▾</span>
                </button>
                {mktOpen ? (
                  <div style={{ position: "absolute", top: ROW_H, left: 0, background: "#fff", borderRadius: 10, border: "1px solid rgba(30,40,34,.1)", boxShadow: "0 8px 24px rgba(10,18,14,.18)", overflow: "hidden", minWidth: 150, zIndex: 300 }}>
                    {item.children.map((s) => (
                      <button key={s.id} type="button" onClick={() => go(s.href)}
                        style={{ display: "flex", width: "100%", padding: "12px 18px", background: "none", border: "none", borderBottom: "1px solid rgba(30,40,34,.08)", fontSize: 13, fontWeight: 800, color: pathActive(pathname, s.href) ? C.gold : "#1f2a24", textAlign: "left", cursor: "pointer" }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(181,154,91,.08)"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = "none"; }}>
                        {navLabel(s, language)}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : (
              <button key={item.id} type="button" onClick={() => go(item.href)} style={btnStyle(groupActive(pathname, item))}>{navLabel(item, language)}</button>
            ))}
          </div>

          <div className="inv-global-nav-desktop inv-global-nav-actions">
            <NavSearch onPick={openTicker} language={language} />
            <NavIconBtn title="Time" ariaLabel="Time"><FiClock /></NavIconBtn>
            <NavIconBtn title="Grid" ariaLabel="Grid"><FiGrid /></NavIconBtn>
            <NavIconBtn title="Notifications" ariaLabel="Notifications"><FiBell /></NavIconBtn>
            <NavIconBtn
              className="inv-nav-lang-btn"
              title={langBtnTitle}
              ariaLabel="Toggle language"
              onClick={toggleLanguage}
            >
              {isEn ? "EN" : "FR"}
            </NavIconBtn>
            {onToggleDark ? (
              <NavIconBtn title={darkTitle} onClick={onToggleDark}>
                <span style={{ fontSize: 16 }}>{darkMode ? "☀" : "☾"}</span>
              </NavIconBtn>
            ) : null}
            <NavIconBtn title={loginTitle} onClick={handleAuthClick}>
              {isLoggedIn ? <FiLogOut /> : <FaUser />}
            </NavIconBtn>
          </div>

          <button type="button" className="inv-global-nav-mobile-btn inv-nav-icon-btn" onClick={() => setMenu((m) => !m)} style={{ marginLeft: "auto" }} aria-label="Menu">
            {menu ? <span style={{ fontSize: 16, lineHeight: 1 }}>×</span> : [0, 1, 2].map((i) => <div key={i} style={{ width: 16, height: 2, borderRadius: 1, background: "rgba(255,255,255,.9)" }} />)}
          </button>
        </div>

        <div className="inv-mkt-subnav" role="navigation" aria-label="Market sections">
          {MARKET_SUBNAV_LINKS.map((link) => (
            <button
              key={link.id}
              type="button"
              className={`inv-mkt-subnav-btn${link.isActive(pathname) ? " active" : ""}`}
              onClick={() => go(link.href)}
            >
              {subnavLabel(link)}
            </button>
          ))}
        </div>
      </div>

      {menu ? (
        <div style={{ background: NAV0, borderTop: "1px solid rgba(255,255,255,.10)", paddingBottom: 10 }}>
          {GLOBAL_NAV.map((item) => item.children ? (
            <div key={item.id}>
              <div style={{ padding: "12px 16px 4px", fontSize: 12, fontWeight: 800, color: C.gold, textTransform: "uppercase", letterSpacing: ".08em" }}>{navLabel(item, language)}</div>
              {item.children.map((s) => (
                <button key={s.id} type="button" onClick={() => go(s.href)}
                  style={{ display: "flex", width: "100%", padding: "11px 16px 11px 28px", background: "none", border: "none", color: pathActive(pathname, s.href) ? C.gold : NAV_TXT, fontSize: 14, fontWeight: 700, borderLeft: pathActive(pathname, s.href) ? "3px solid " + C.gold : "3px solid transparent", cursor: "pointer" }}>
                  {navLabel(s, language)}
                </button>
              ))}
            </div>
          ) : (
            <button key={item.id} type="button" onClick={() => go(item.href)}
              style={{ display: "flex", width: "100%", padding: "14px 16px", background: "none", border: "none", color: groupActive(pathname, item) ? C.gold : NAV_TXT, fontSize: 15, fontWeight: 800, borderLeft: groupActive(pathname, item) ? "3px solid " + C.gold : "3px solid transparent", cursor: "pointer" }}>
              {navLabel(item, language)}
            </button>
          ))}
          <div style={{ borderTop: "1px solid rgba(255,255,255,.1)", marginTop: 8, paddingTop: 8 }}>
            {MARKET_SUBNAV_LINKS.map((link) => (
              <button key={link.id} type="button" onClick={() => go(link.href)}
                style={{ display: "flex", width: "100%", padding: "12px 16px", background: "none", border: "none", color: link.isActive(pathname) ? C.gold : NAV_TXT, fontSize: 14, fontWeight: 800, cursor: "pointer" }}>
                {subnavLabel(link)}
              </button>
            ))}
          </div>
          <div style={{ display: "flex", gap: 10, padding: "12px 16px", flexWrap: "wrap", alignItems: "center" }}>
            <NavIconBtn title="Time" ariaLabel="Time"><FiClock /></NavIconBtn>
            <NavIconBtn title="Grid" ariaLabel="Grid"><FiGrid /></NavIconBtn>
            <NavIconBtn title="Notifications" ariaLabel="Notifications"><FiBell /></NavIconBtn>
            <NavIconBtn className="inv-nav-lang-btn" title={langBtnTitle} ariaLabel="Toggle language" onClick={toggleLanguage}>
              {isEn ? "EN" : "FR"}
            </NavIconBtn>
            {onToggleDark ? (
              <NavIconBtn title={darkTitle} onClick={onToggleDark}>
                <span style={{ fontSize: 16 }}>{darkMode ? "☀" : "☾"}</span>
              </NavIconBtn>
            ) : null}
            <NavIconBtn title={loginTitle} onClick={handleAuthClick}>
              {isLoggedIn ? <FiLogOut /> : <FaUser />}
            </NavIconBtn>
          </div>
        </div>
      ) : null}
    </nav>
  );
}
